import { VStack, HStack, Flex, Divider, Box, SkeletonText } from "@chakra-ui/react";
import MeetingLinksFields from "features/Resources/Meeting/Components/MeetingLinksFields";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { Text14 } from "components/Text";
import { ShowAttendees } from "features/Resources/Meeting/Components/DisplayAttendees";
import CDS, { AdminMeetingTypeTag } from "design-system";
import { AttendeeNameTag } from "components/Tag/ResourceTargetTag/MeetingAttendeeTag";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import ScheduledAtTag from "features/Resources/Meeting/Components/ScheduledAtTag";
import InfoOutlineRounded from "components/Icon/Iconify/InfoOutlineRounded";
import LinkButton from "components/Button/ExternalLinkButton";
import { parseDateOnly } from "helpers/time";
import { Meeting } from "../models";

interface MeetingDetailsProps {
  meeting: Meeting;
  showHeader?: boolean;
  showEditLink?: boolean;
  isLoading?: boolean;
  journeyStartDate?: Date | string;
}

export default function MeetingDetails({ meeting, showHeader = true, showEditLink = true, isLoading = false, journeyStartDate }: MeetingDetailsProps) {
  const leadAttendee = meeting.attendees.find((a) => a.isLead);
  const isBeforeJourneyStart = journeyStartDate
    ? new Date() < parseDateOnly(journeyStartDate)
    : false;

  return (
    <VStack spacing={2} align="stretch" width="100%">
      {showHeader && (
        <>
          <VStack align="start" spacing="3">
            <HStack spacing="3">
              <AdminMeetingTypeTag.IconBadge meetingType={meeting.meetingType} />
              <Flex align="center" gap="2">
                <Text14 fontWeight="bold" fontSize="lg">
                  {meeting.name}
                </Text14>
              </Flex>
            </HStack>
            <ScheduledAtTag
              scheduledAt={meeting.startTime}
              timezone={meeting.timezone}
              durationMinutes={meeting.durationMinutes}
              status={meeting.status}
              width="100%"
            />
          </VStack>
          <Divider my="2" />
        </>
      )}

      <ShowAttendees
        label="Attendees"
        attendees={leadAttendee ? meeting.attendees.filter((a) => a.userId !== leadAttendee.userId) : meeting.attendees}
        leadId={leadAttendee?.userId}
      />

      {leadAttendee && (
        <HorizontalGreyBlackField
          label="Lead"
          labelProps={{ marginBottom: 0 }}
          labelContainerProps={longAttributeStyles.labelContainerProps}
        >
          <AttendeeNameTag
            name={leadAttendee.user?.fullName || leadAttendee.emailAddress}
            responseStatus={leadAttendee.responseStatus}
          />
        </HorizontalGreyBlackField>
      )}

      <MeetingLinksFields
        videoConferenceUrl={isBeforeJourneyStart ? undefined : meeting.videoConferencingUrl}
        videoConferenceProvider={meeting.videoConferencingProvider}
        externalUrl={isBeforeJourneyStart ? undefined : meeting.externalUrl}
      />

      <Box my="1" />

      {[
        [meeting.purposeDisplay, "Purpose"],
        [meeting.learningObjectiveDisplay, "Learning Objective"],
        [meeting.additionalInfoDisplay, "Additional Info"],
      ].map(
        ([richText, label]) =>
          richText && label && <ShowRichText key={`rich-text-${label}`} label={label} richText={richText} />,
      )}

      {showEditLink && !isBeforeJourneyStart && meeting.externalUrl && (
        <VStack mt="4" spacing="3" align="stretch">
          <Box p="5" bg="cdsIndigo.50" borderWidth="1px" borderColor="cdsIndigo.600" borderRadius="md">
            <VStack align="start" spacing="1">
              <InfoOutlineRounded boxSize="6" color="cdsIndigo.600" />
              <CDS.Text14 fontWeight="semibold" color="cdsIndigo.600">
                Need to make changes to this meeting?
              </CDS.Text14>
              <CDS.Text12 color="cdsGrey.800">
                All meeting details are synced with Google Calendar. To edit participants, time, or any other details,
                please use the button below to open this event in Google Calendar.
              </CDS.Text12>
            </VStack>
          </Box>
          <LinkButton href={meeting.externalUrl} width="100%">
            Edit in Google Calendar
          </LinkButton>
        </VStack>
      )}

      {isLoading && <SkeletonText noOfLines={5} />}
    </VStack>
  );
}
